import React from "react";
import { useItemList } from "../utils/hooks.ts";
import HCarousel from "../itemlist-views/hcarousel/HCarousel.tsx";
import VCarousel from "../itemlist-views/vcarousel/VCarousel.tsx";
import FeaturedItems from "../itemlist-views/featureditems/FeaturedItems.tsx";
import Grid from "../itemlist-views/grid/Grid.tsx";
import HnCarousel from "../itemlist-views/hncarousel/HnCarousel.tsx";
import { getOrFail } from "../utils/common-utils.ts";
import ReactDOM from "react-dom/client";
import Loading from "../loading/Loading.tsx";
import ItemListErrorBoundary from "./ItemListErrorBoundary.tsx";

// Watchdog timing. The list widget never gives up: it polls while healthy and, once lost,
// backs off exponentially up to MAX_RETRY, then resets to a fast burst (sawtooth) so a tab
// left open through a bad-data window (e.g. waiting for a livestream to go live) eventually
// recovers instead of staying blank forever.
const HEALTHY_DELAY = 5_000; // health-check cadence while the list renders fine
const MIN_RETRY = 1_000; // first attempt of each recovery burst
const MAX_RETRY = 15 * 60_000; // backoff ceiling; reaching it resets to MIN_RETRY (sawtooth)

export type ItemListPropTypes = {
  pageLocalId: string;
  listName: string;
  fadeOutSeconds: number;
  itemId?: string;
  // Bumped by the watchdog on each recovery re-render; flows into the SWR key so a recovery
  // fetches fresh data instead of replaying the cached bad list.
  generation?: number;
  // When set, the list loads index-natively from the single-list endpoint by component localId.
  // Every mount carries one typed id (componentLocalId / programId / videoEpisodeId / landingId).
  componentLocalId?: string;
  removeCpacTv?: boolean; // re-added July 8, 2026
  // When set, the list loads from the program episode-list endpoint (item-list-program.json?id) —
  // the per-request program-filtered list. Shared by the side carousel and the featured hero.
  programId?: string;
  // When set, the list loads from the video (episode) page endpoint (item-list-video.json?id) — the
  // per-request "latest videos" list whose live-vs-VOD shape the endpoint derives from the episode.
  videoEpisodeId?: string;
  // When set, the list loads from the landing page endpoint (item-list-landing.json?id) — the program's
  // episodes rendered with the landing's own presentation (h_carrousel + full_episodes_title + pageSize).
  landingId?: string;
};

export const renderItemList = (div: string) => {
  const root = document.getElementById(div);

  if (root) {
    const path = getOrFail(root.dataset, "localid");
    const name = getOrFail(root.dataset, "list");
    const fade = parseInt(getOrFail(root.dataset, "fade"));
    const itemId = root.dataset["itemid"] ?? "";
    // A list component localId routes this mount through the index-native single-list endpoint.
    const component = root.dataset["component"] ?? "";
    const removeCpacTv = root.dataset["removecpactv"] === "true";
    // Program pages stamp the program id here so the list loads from the id-parameterized endpoint.
    const programId = root.dataset["programId"] ?? "";
    // Video (episode) pages stamp the episode id here for the video latest-videos endpoint.
    const videoEpisodeId = root.dataset["videoId"] ?? "";
    // Landing pages stamp the landing localId here for the landing latest-videos endpoint.
    const landingId = root.dataset["landingId"] ?? "";

    const holder = document.createElement("div");
    root.appendChild(holder);
    // Create a single root and reuse it — never call createRoot() again after this
    const reactRoot = ReactDOM.createRoot(holder);

    let generation = 0;
    let retryDelay = MIN_RETRY;
    let recovering = false;

    const doRender = () => {
      reactRoot.render(
        <React.StrictMode>
          {/* key={generation} remounts the boundary on recovery so its latched hasError
              resets; generation also flows into the SWR key (see useItemList) so the
              recovery re-render fetches fresh data instead of replaying the cached bad list. */}
          <ItemListErrorBoundary key={generation} listName={name}>
            <ItemList
              pageLocalId={path}
              listName={name}
              fadeOutSeconds={fade}
              itemId={itemId}
              generation={generation}
              componentLocalId={component}
              removeCpacTv={removeCpacTv}
              programId={programId}
              videoEpisodeId={videoEpisodeId}
              landingId={landingId}
            />
          </ItemListErrorBoundary>
        </React.StrictMode>,
      );
    };

    doRender();

    // ±20% jitter so tabs that loaded at the same moment don't retry in lockstep and herd
    // the item-list service.
    const jitter = (ms: number) => ms * (0.8 + Math.random() * 0.4);

    function scheduleNext(ms: number) {
      setTimeout(tick, jitter(ms));
    }

    function tick() {
      // "Lost" = an empty holder (nothing rendered) OR the boundary's marked failure div.
      // The marked-div check fixes the false-healthy case: an error fallback is a non-empty
      // child that a naive childNodes-length check mistook for a healthy render.
      const lost =
        holder.childNodes.length === 0 ||
        holder.querySelector("[data-list-failed]") !== null;

      if (lost) {
        recovering = true;
        generation++; // bust the SWR key so the re-render fetches fresh data
        doRender();
        console.log(
          `Livestream list lost. Re-rendering; next check in ~${Math.round(retryDelay / 1000)}s.`,
        );
        scheduleNext(retryDelay);
        // Back off for next time; once the gap reaches the ceiling, reset to a fast burst
        // (sawtooth) so a long-open tab keeps getting periodic aggressive retries.
        retryDelay =
          retryDelay >= MAX_RETRY ? MIN_RETRY : Math.min(retryDelay * 2, MAX_RETRY);
      } else {
        if (recovering) {
          console.log("Livestream list recovered.");
          recovering = false;
        }
        retryDelay = MIN_RETRY; // reset backoff for the next time it goes dead
        scheduleNext(HEALTHY_DELAY);
      }
    }

    scheduleNext(HEALTHY_DELAY);
  }
};

const ItemList = (props: ItemListPropTypes) => {
  const { pageLocalId, listName, fadeOutSeconds, itemId, generation, componentLocalId, removeCpacTv, programId, videoEpisodeId, landingId } = props;
  const { loading, list, error } = useItemList(listName, pageLocalId, itemId, generation, componentLocalId, removeCpacTv, undefined, programId, videoEpisodeId, landingId);

  if (loading) {
    return <Loading />;
  }
  if (error) {
    console.error("An error has occurred.", error);
    return;
  }
  if (list === undefined) {
    // console.debug("Nothing to render", {pageLocalId, listName})
    return;
  }

  // console.debug("About to render", {id, pageLocalId, listName, fadeOutSeconds, itemList, filteredItems})
  switch (list.viewmode) {
    case "h_carrousel_with_f_number":
      // Prefer the fade carried on the list (index-native home tiles); fall back to the
      // data-fade prop for any legacy mount that still supplies it.
      return <HCarousel itemList={list} fadeOutSeconds={list.fadeOutSeconds ?? fadeOutSeconds} />;
    case "v_carrousel_view":
      return <VCarousel itemList={list} />;
    case "h_carrousel_view":
      return <HnCarousel itemList={list} />;
    case "f_item_view":
      return <FeaturedItems itemList={list} />;
    case "grid":
      return <Grid itemList={list} />;
    default:
      console.warn("Unsupported ItemList view", list.viewmode);
      return;
  }
};

export default ItemList;
